import {Title} from '@mantine/core';
import {Notifications} from '@mantine/notifications';
import type React from 'react';
import {
  isReleasedAiToDoCreationFlow,
  isReleasedCareerAdviser,
} from '../../../../../config/unReleasePage';
import {SP_FILTER_CONFIG} from '../../../usecase/useProgressStatusFilter';
import {MissionsCollapse} from '../Presenter/Missions/MissionsCollapse';
import {AddEntryButton} from '../Presenter/Progress/AddEntryButton';
import {AiTodoGenerateButton} from '../Presenter/Progress/AiTodoGenerateButtonWithTooltip';
import {CompanySearch} from '../Presenter/Progress/CompanySearch';
import {InterviewTrainingLink} from '../Presenter/Progress/InterviewTrainingLink';
import {LoadMoreButton} from '../Presenter/Progress/LoadMoreButton';
import {ProgressCard} from '../Presenter/Progress/ProgressCard';
import {ReminderText} from '../Presenter/Progress/ReminderText';
import type {ProgressPageComponentProps} from './ProgressPagePcComponent';
import {ProgressSort} from './ProgressSort';
import {ProgressStatusFilter} from './ProgressStatusFilter';

/**
 * SP版コンポーネント（進捗一覧ページ用）
 * ドロワー表示で詳細を開く形式
 * ステータスフィルターはAPI側で処理するため、propsから受け取る
 */
export const ProgressPageSpComponent: React.FC<ProgressPageComponentProps> = ({
  entryManagementId,
  handleSort,
  requireAuth,
  companyList,
  companySearchWord,
  setCompanySearchWord,
  onCompanySearchSubmit,
  filteredEntries,
  totalCount,
  reminderTexts,
  handleAddEntryManagement,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  progressMissions,
  selectedStatus = 'all',
  onStatusChange,
  handleAiTodoGenerate,
  canOpenDetail,
}) => {
  // 選択されたステータスのタイトルを取得
  const selectedStatusTitle = SP_FILTER_CONFIG[selectedStatus].title;

  return (
    <>
      <Notifications position="top-right" />

      {/* ヘッダー部分 */}
      <div className="w-full flex flex-col gap-8 bg-white px-16 py-16 rounded-16">
        <div className="flex flex-col gap-12">
          <div className="flex gap-12 justify-between">
            <Title order={1} className="text-xl">
              選考管理
            </Title>
            <AddEntryButton onClick={handleAddEntryManagement} variant="sp" />
          </div>

          <div className="flex gap-8 items-center">
            {isReleasedCareerAdviser() && <InterviewTrainingLink />}
            {isReleasedAiToDoCreationFlow() && handleAiTodoGenerate && (
              <AiTodoGenerateButton onClick={handleAiTodoGenerate} isLarge />
            )}
          </div>
        </div>
        <ReminderText texts={reminderTexts} />
      </div>

      {/* ミッション一覧（SP版） */}
      <MissionsCollapse missions={progressMissions} />

      {/* 検索・フィルター・ソート */}
      <div className="w-full flex items-center gap-8 bg-white px-16 py-8 rounded-16">
        <CompanySearch
          companyList={companyList}
          value={companySearchWord}
          onChange={setCompanySearchWord}
          onSubmit={onCompanySearchSubmit}
          variant="sp"
        />
        {onStatusChange && (
          <ProgressStatusFilter
            selectedStatus={selectedStatus}
            onStatusChange={onStatusChange}
          />
        )}
        <ProgressSort handleSort={handleSort} requireAuth={requireAuth} />
      </div>

      {/* 現在のステータス表示 */}
      <div className="w-full px-4">
        <span className="text-sm font-bold text-black-1">
          {selectedStatusTitle}
        </span>
        <span className="text-sm text-black-2 ml-8">{totalCount}件</span>
      </div>

      {/* カードリスト */}
      <div className="flex flex-col w-full gap-16">
        {filteredEntries.length > 0 ? (
          filteredEntries.map((entry) => (
            <ProgressCard
              key={entry.id}
              entry={entry}
              isSelected={entry.id === entryManagementId}
              isSp
              canOpenDetail={canOpenDetail}
            />
          ))
        ) : (
          <div className="text-sm text-black-1 text-center p-16 bg-white rounded-16">
            該当するエントリーがありません
          </div>
        )}
        {filteredEntries.length > 0 && fetchNextPage && (
          <LoadMoreButton
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage ?? false}
          />
        )}
      </div>
    </>
  );
};
